import React from "react";

import { SvgIcon, type SvgIconProps } from "@mui/material";

/**
 * Base wrapper for custom Rusha icons.
 *
 * All custom domain icons use this component to ensure consistent sizing,
 * viewBox, and stroke/fill conventions across the icon set.
 *
 * Visual spec (Dual-tone layered):
 * - 24x24 viewBox, 2px outer padding → 20x20 live area
 * - 2px stroke, round caps, round joins
 * - Outlines use currentColor (inherits text color)
 * - Accent fills use brand colors at low opacity (6-15%)
 * - Node dots on key domain icons (Stack, Pipeline, Connection)
 *
 * Usage:
 *   <RushaSvgIcon>
 *     <path d="..." stroke="currentColor" strokeWidth={2} ... />
 *     <circle cx="12" cy="5" r="8" fill="#00d4ff" fillOpacity={0.12} />
 *   </RushaSvgIcon>
 */
export const RushaSvgIcon = React.forwardRef<SVGSVGElement, RushaSvgIconProps>(
  function RushaSvgIcon({ children, sx, size, ...props }, ref) {
    return (
      <SvgIcon
        ref={ref}
        viewBox="0 0 24 24"
        sx={{
          width: size ?? 24,
          height: size ?? 24,
          ...sx,
        }}
        {...props}
      >
        {children}
      </SvgIcon>
    );
  },
);

export interface RushaSvgIconProps extends Omit<SvgIconProps, "ref"> {
  /** Icon size in pixels. Defaults to 24 (inherits from SvgIcon default). */
  size?: number;
  children: React.ReactNode;
}
